import Data from '@/data'
import { ItemFlags } from '@/data/enums'
import Trans from '@/data/trans'
import { Range } from '@/data/types'
import { itemGetMods, itemMaxAffixes, itemTags, itemTranslationDomain, itemUpdateStats } from '@/logic/item'
import {
  ModGroup,
  checkCondition,
  evaluateTags,
  forceCondition,
  influenceFlags,
  modStats,
  modStatsMax,
  modsGroupMods
} from '@/logic/mods'
import { Item, StatType } from '@/types/item'
import { SelectOption, underlinerFunc } from '@/ui/select'
import Tooltip from '@/ui/tooltip'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SvgIcon } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import React, { ReactNode } from 'react'
import { ItemProps } from './ItemEditor'
import { TextLine } from './TextLine'
import { AbstractCategory, categoryNames } from './category'
import { SelectLine } from './select'

const generationTypes: Record<string, string> = {
  unique: 'Normal',
  prefix: 'Prefixes',
  suffix: 'Suffixes',
  corrupted: 'Corrupted',
  enchantment: 'Enchantments',
  flask_enchantment_instilling: 'Enchantments'
}

const ModTiers = ({ group, current, domain }: { group: ModGroup; domain: string; current?: string }) => (
  <div className="poe-ModTiers">
    {group.mods.map((id) => {
      const mod = Data.mods[id]
      const stats = modStats(mod)
      const lines = Trans[domain].formatStats(stats, false, true)
      const master =
        mod.domain === 'master' && Data.crafting_bench_options.find((opt) => opt.actions?.add_explicit_mod === id)
      const name = master ? `${master.master}${master.bench_tier ? `T${master.bench_tier}` : ''}` : mod.name
      return (
        <div key={id} className={classNames({ 'poe-current': id === current })}>
          iLvl {mod.required_level}: {lines.join(' / ')}
          {name ? ` (${name})` : ''}
        </div>
      )
    })}
  </div>
)

function ItemModTiers({
  item,
  type,
  mod,
  itemMods
}: {
  item: Item
  type: StatType
  mod: string
  itemMods: ReturnType<typeof itemGetMods>
}) {
  const mods = itemMods[type]
  const groups = mods && modsGroupMods(mods)
  const group = groups?.groups[groups.lookup[mod]]
  if (!group) return null
  return <ModTiers group={group} domain={itemTranslationDomain(Data.items[item.base])} />
}

type ModOption = {
  id: string
  name: string
  disabled?: string
}

export function ModLine({
  item,
  onChange,
  id,
  type,
  itemMods
}: {
  id?: string
  type: StatType
  itemMods: ReturnType<typeof itemGetMods>
} & ItemProps) {
  const onRemove = React.useCallback(
    () =>
      onChange((item) => {
        item = { ...item }
        item.mods = { ...item.mods! }
        item.mods[type] = { ...item.mods[type] }
        if (id) delete item.mods[type]![id]
        return itemUpdateStats(item, type)
      }),
    [id, type, onChange]
  )
  const onSelect = React.useCallback(
    (opt?: SelectOption<string>) =>
      opt &&
      onChange((item) => {
        if (item.mods?.[type]?.[opt.id]) return item
        item = { ...item }
        item.mods = { ...item.mods! }
        const entries = Object.entries(item.mods[type] ?? {})
        const index = entries.findIndex((e) => e[0] === id)
        entries[index >= 0 ? index : entries.length] = [opt.id, modStatsMax(Data.mods[opt.id])]
        item.mods[type] = Object.fromEntries(entries)
        return itemUpdateStats(item, type)
      }),
    [id, type, onChange]
  )
  const onStatChange = React.useCallback(
    (stat: string, value: number) =>
      onChange((item) => {
        if (!id) return item
        const stats = item.mods![type]?.[id]
        if (!stats || stats[stat] === value) return item
        const mStats = modStats(Data.mods[id])

        function fallback() {
          item = { ...item }
          item.mods = { ...item.mods! }
          const mods = (item.mods[type] = { ...item.mods[type] })
          mods[id!] = { ...stats, [stat]: value }
          return itemUpdateStats(item, type)
        }

        if (mStats[stat] && checkCondition(mStats[stat], value)) {
          return fallback()
        }
        const mods = itemGetMods(item, true)[type]
        const groups = mods && modsGroupMods(mods)
        const group = groups?.groups[groups.lookup[id]]
        if (!group) return fallback()

        // find next best mod
        let next: string | undefined
        let nextStats: Record<string, Range> | undefined
        for (const mod of group.mods) {
          const mStats = modStats(Data.mods[mod])
          if (!mStats[stat]) continue
          if (value < stats[stat]) {
            // value decreased, find mod such that mod.min <= value, and mod.max is as high as possible
            if (mStats[stat].min <= value && (!nextStats || mStats[stat].max > nextStats[stat].max)) {
              next = mod
              nextStats = mStats
            }
          } else {
            // value increased, find mod such that mod.max >= value, and mod.min is as low as possible
            if (mStats[stat].max >= value && (!nextStats || mStats[stat].min < nextStats[stat].min)) {
              next = mod
              nextStats = mStats
            }
          }
        }
        if (!next) return fallback()

        item = { ...item }
        item.mods = { ...item.mods! }
        const entries = Object.entries(item.mods[type] ?? {})
        const index = entries.findIndex((m) => m[0] === id)
        entries[index >= 0 ? index : entries.length] = [
          next,
          Object.fromEntries(
            Object.entries(nextStats!).map(([sid, sv]) => [
              sid,
              forceCondition(sv, sid === stat ? value : stats[sid] || 0)
            ])
          )
        ]
        item.mods[type] = Object.fromEntries(entries)
        return itemUpdateStats(item, type)
      }),
    [id, type, onChange]
  )

  const empty = React.useMemo(() => {
    const mods = itemMods[type]
    const groups = mods && modsGroupMods(mods)
    return !mods || !id || groups?.lookup[id] == null
  }, [id, itemMods, type])

  const contents = React.useCallback(
    (expanded: boolean) => {
      const mods = itemMods[type]
      const groups = mods && modsGroupMods(mods)
      if (!mods || !id || groups?.lookup[id] == null) {
        return <div>{`<Add ${categoryNames[type]} Mod>`}</div>
      }
      const group = groups.groups[groups.lookup[id]]
      const stats = item.mods![type]?.[id] ?? {}
      const domain = itemTranslationDomain(Data.items[item.base])
      const lines = Trans[domain].maskFromStatList(stats)
      const result = Object.keys(lines)
        .map(Number)
        .map((index) => {
          const limits = Trans.all[index].ids.map((stat) => group.stats[stat])
          return (
            <TextLine
              domain={domain}
              key={index}
              id={index}
              stats={stats}
              limits={limits}
              onChange={onStatChange}
              disabled={expanded}
            />
          )
        })
      const { generation_type: gen, is_essence_only } = mods[id]
      let tierInfo: ReactNode
      if (gen === 'suffix' || gen === 'prefix') {
        const tier = group.mods.length - 1 - group.mods.indexOf(id)
        tierInfo = (
          <Tooltip tooltip={<ModTiers group={group} current={id} domain={domain} />}>
            <span className="poe-label">
              {gen === 'suffix' ? 'S' : 'P'}
              {is_essence_only ? 'E' : ''}
              {tier}
            </span>
          </Tooltip>
        )
      }
      if (!expanded) {
        result.push(
          <div className="poe-mods-info" key="info">
            {tierInfo}
            <SvgIcon icon="chevron-down" className="poe-arrow" />
          </div>
        )
      }
      return result
    },
    [id, type, item, itemMods, onStatChange]
  )

  const getOptions = React.useCallback(() => {
    const mods = itemGetMods(item)[type]
    if (!mods) return []
    const groups = modsGroupMods(mods)
    const used = new Set<string>()
    const tags = itemTags(item)
    let essence = 0,
      prefix = 0,
      suffix = 0
    for (const [mt, mods] of Object.entries(item.mods!)) {
      if ((mt === 'implicit') !== (type === 'implicit')) continue
      for (const mid of Object.keys(mods)) {
        if (mid === id) continue
        const mod = Data.mods[mid]
        if (mod.is_essence_only) essence = 1
        if (mod.generation_type === 'prefix') prefix += 1
        if (mod.generation_type === 'suffix') suffix += 1
        used.add(mod.group)
        if (mod.adds_tags) {
          for (const tag of mod.adds_tags) tags.add(tag)
        }
      }
    }
    const maxAffix = itemMaxAffixes(item)
    const optgroups: Record<string, ModOption[]> = {}
    for (const g of Object.keys(generationTypes)) optgroups[g] = []

    const domain = itemTranslationDomain(Data.items[item.base])

    for (const group of groups.groups) {
      if (!optgroups[group.generation_type]) continue
      let disabled: string | undefined
      if (used.has(group.group)) {
        disabled = `Mod of this type already present on the item`
      } else if (group.is_essence_only && essence) {
        disabled = `Another essence mod already present on the item`
      } else if (group.generation_type === 'prefix' && prefix >= maxAffix) {
        disabled = `No space for more prefixes`
      } else if (group.generation_type === 'suffix' && suffix >= maxAffix) {
        disabled = `No space for more suffixes`
      }
      const mod = group.mods[group.mods.length - 1]
      if (
        !disabled &&
        Data.mods[mod].generation_weights.length &&
        !evaluateTags(Data.mods[mod].generation_weights, tags)
      ) {
        disabled = 'Conflicting mod present on the item'
      }
      let text = Trans[domain].formatStats(group.stats).join(' / ')
      if (group.is_essence_only) text += ` (Essence)`
      for (const { flag, name } of influenceFlags) {
        if (group.influence & flag) text += ` (${name})`
      }
      optgroups[group.generation_type].push({ id: mod, disabled, name: text })
    }
    return Object.entries(optgroups)
      .map(([g, options]) => ({ title: generationTypes[g], options }))
      .filter((opt) => opt.options.length)
  }, [id, item, type])

  const renderOption = React.useCallback(
    ({ id, name, disabled }: ModOption, search?: RegExp) => (
      <Tooltip tooltip={disabled || <ItemModTiers item={item} type={type} mod={id} itemMods={itemMods} />}>
        <span>{underlinerFunc(name, search)}</span>
      </Tooltip>
    ),
    [item, type, itemMods]
  )

  let removable = !empty
  if (type === 'implicit' && removable) {
    removable = !Data.items[item.base].implicits.some((mod) => Data.mods[mod].visible)
  }

  return (
    <li className={classNames('poe-StatLine', { 'poe-empty': empty })}>
      {!!removable && (
        <span className="poe-remove" title="Remove stat" onClick={onRemove}>
          <FontAwesomeIcon icon={faTimes} />
        </span>
      )}
      <SelectLine options={getOptions} renderOption={renderOption} id={id} onChange={onSelect}>
        {contents}
      </SelectLine>
    </li>
  )
}

export function ModCategory({
  item,
  onChange,
  type
}: {
  type: StatType
} & ItemProps) {
  const mods = item.mods![type] ?? {}
  const lines = Object.keys(mods).sort((a, b) => Data.mods[a]?.order - Data.mods[b]?.order)
  const emptyLines = React.useMemo(() => {
    if (type === 'implicit') {
      let limit = 1
      if (item.flags & ItemFlags.Corrupted) limit = Math.max(limit, 2)
      return limit - lines.length
    } else if (type === 'enchant') {
      return 1 - lines.length
    } else {
      let prefix = 0,
        suffix = 0
      for (const [mt, mods] of Object.entries(item.mods!)) {
        if (mt === 'implicit') continue
        for (const id of Object.keys(mods)) {
          const mod = Data.mods[id]
          if (mod?.generation_type === 'prefix') prefix += 1
          if (mod?.generation_type === 'suffix') suffix += 1
        }
      }
      const maxAffix = itemMaxAffixes(item)
      if (type === 'crafted') {
        if (lines.length > 0 && !item.stats.crafted['item_generation_can_have_multiple_crafted_mods']) {
          return 0
        }
      }
      return prefix < maxAffix || suffix < maxAffix ? 1 : 0
    }
  }, [item, type, lines.length])

  const itemMods = React.useMemo(() => itemGetMods(item, true), [item])

  if (!lines.length && !emptyLines) {
    return null
  }

  return (
    <AbstractCategory name={categoryNames[type]} className={classNames({ 'poe-item-mod-view': type !== 'implicit' })}>
      {lines.map((id, index) => (
        <ModLine key={index} item={item} onChange={onChange} id={id} type={type} itemMods={itemMods} />
      ))}
      {emptyLines > 0 &&
        [...Array(emptyLines)].map((_, index) => (
          <ModLine key={`empty${index}`} item={item} onChange={onChange} type={type} itemMods={itemMods} />
        ))}
    </AbstractCategory>
  )
}
